import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tab, Tabs, Spinner } from "react-bootstrap";
import { Localization, SessionContext, SettingsManager, AppMessagesContext, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonDropdown, GlassButtonRow } from "src/components/GlassButtons";
import { ConfirmationDialog } from "src/components/ConfirmationDialog";
import { actionSetMapInteractionState, MapInteractionState, MapitStateContext } from "src/states/MapitState";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { closeWindow, ManagedWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { LayerInfo, LayerType } from "src/common/managers/Types";
import { PLADataServiceImpl, PLAProjectServiceImpl } from "./PLAPersistence";
import { PLAElementRecord, PLAProject, PLARelationRecord, PLALocalStorageSelectedProjectKey, getMyPLAAccessRights, hasPLAWriteAccess } from "./PLAState";
import { getPLAAlerts, validatePLAData } from "./PLAExpression";
import { buildPLAZoomFeature, removePLAMapLayers, setPLASelectedFeature, setupPLAMapLayers } from "./PLAMapLayers";
import { PLAElementDialog, createEmptyPLAElement, PLARelationDialog } from "./PLAElementDialog";
import { PLATable, PLATableRef } from "./PLATable";

export function PLAWindow(props: { managedWindow: ManagedWindow }) {
   const { state: sessionState } = useContext(SessionContext);
   const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);

   const { dispatch: windowDispatch } = useWindowContext();
   const [projects, setProjects] = useState<PLAProject[]>([]);
   const [selectedProjectId, setSelectedProjectId] = useState<string>(localStorage.getItem(PLALocalStorageSelectedProjectKey) || "");
   const [elements, setElements] = useState<PLAElementRecord[]>([]);
   const [relations, setRelations] = useState<PLARelationRecord[]>([]);
   const [loading, setLoading] = useState(false);
   const [dirty, setDirty] = useState(false);
   const [activeTab, setActiveTab] = useState<string | undefined>();
   const [visibleElementTypes, setVisibleElementTypes] = useState<Record<string, boolean>>({});
   const [selectedElementId, setSelectedElementId] = useState<string | undefined>();
   const [showElementEditor, setShowElementEditor] = useState<PLAElementRecord | undefined>();
   const [showRelationEditor, setShowRelationEditor] = useState<PLAElementRecord | undefined>();
   const [confirmDeleteElement, setConfirmDeleteElement] = useState<PLAElementRecord | undefined>();
   const [confirmClose, setConfirmClose] = useState(false);
   const tableRefs = useRef<Record<string, PLATableRef | null>>({});
   const selectedProject = projects.find((project) => project.id === selectedProjectId);
   const mapFacade = mapitState.map;

   const availableLayers = useMemo(() => Object.values(mapitState.layers || {}).filter((layer: LayerInfo) => [LayerType.GeoJSON_Point, LayerType.GeoJSON_Polygon, LayerType.GeoJSON].includes(layer.type)), [mapitState.layers]);
   const writable = hasPLAWriteAccess(selectedProject, sessionState.userRef, sessionState.customerRef) || hasAccessToFeature(Feature.PLAToolWriteAccess);
   const readOnly = !writable;
   const mapElements = useMemo(() => elements.filter((element) => visibleElementTypes[element.elementType] !== false), [elements, visibleElementTypes]);

   const projectService = () => new PLAProjectServiceImpl(SettingsManager.getSystemSetting("pla.projectsElasticIndex", ""), sessionState.userName || sessionState.userRef);
   const dataService = (index: string) => new PLADataServiceImpl(index, sessionState.userName || sessionState.userRef);

   useEffect(() => {
      mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Override));

      return () => {
         mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Normal));
      };
   }, []);

   useEffect(() => {
      projectService().loadProjects()
         .then((loadedProjects) => {
            const visibleProjects = loadedProjects.filter((project) => {
               if (hasAccessToFeature(Feature.PLAToolGlobalAdmin)) return true;
               return getMyPLAAccessRights(project, sessionState.userRef, sessionState.customerRef).length > 0;
            });
            setProjects(visibleProjects);
            if (!selectedProjectId && visibleProjects[0]) setSelectedProjectId(visibleProjects[0].id);
         })
         .catch((error) => appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to load projects")}: ${error.message || error}`)));
   }, [sessionState.userName, sessionState.userRef, sessionState.customerRef]);

   useEffect(() => {
      if (!selectedProject) return;
      localStorage.setItem(PLALocalStorageSelectedProjectKey, selectedProject.id);
      setLoading(true);
      Promise.all([dataService(selectedProject.esIndexName).loadElements(), dataService(selectedProject.esIndexName).loadRelations()])
         .then(([loadedElements, loadedRelations]) => {
            setElements(loadedElements);
            setRelations(loadedRelations);
            setDirty(false);
            const firstType = Object.entries(selectedProject.elementTypes || {}).sort((a, b) => a[1].sortOrder - b[1].sortOrder)[0]?.[0];
            setActiveTab(firstType);
            setVisibleElementTypes(Object.keys(selectedProject.elementTypes || {}).reduce<Record<string, boolean>>((acc, elementTypeId) => {
               acc[elementTypeId] = true;
               return acc;
            }, {}));
         })
         .catch((error) => appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to load project data")}: ${error.message || error}`)))
         .finally(() => setLoading(false));
   }, [selectedProject?.id]);

   const alertsByElementId = useMemo(() => {
      if (!selectedProject) return {};
      return elements.reduce<Record<string, string[]>>((acc, element) => {
         acc[element.id] = getPLAAlerts(element, selectedProject, elements, relations);
         return acc;
      }, {});
   }, [selectedProject, elements, relations]);

   const alertCountByElementType = useMemo(() => {
      return elements.reduce<Record<string, number>>((acc, element) => {
         if ((alertsByElementId[element.id] || []).length > 0) acc[element.elementType] = (acc[element.elementType] || 0) + 1;
         return acc;
      }, {});
   }, [elements, alertsByElementId]);

   useEffect(() => {
      const map = mapFacade?.getMapPop?.();
      if (!map || !selectedProject) return;
      const cleanup = setupPLAMapLayers(map, mapElements, alertsByElementId, {
         onFeatureClick: (elementId) => {
            const element = elements.find((item) => item.id === elementId);
            if (!element) return;
            setPLASelectedFeature(map, elementId);
            setSelectedElementId(elementId);
            setActiveTab(element.elementType);
            window.setTimeout(() => tableRefs.current[element.elementType]?.scrollToRow(elementId, true), 0);
         }
      }, selectedProject.elementTypes || {});
      return cleanup;
   }, [mapFacade, elements, mapElements, alertsByElementId, selectedProject?.id]);

   useEffect(() => {
      return () => removePLAMapLayers(mapFacade?.getMapPop?.());
   }, [mapFacade]);

   const zoomToElement = (element: PLAElementRecord) => {
      setSelectedElementId(element.id);
      const map = mapFacade?.getMapPop?.();
      if (map) setPLASelectedFeature(map, element.id);
      const feature = buildPLAZoomFeature(element);
      if (feature) mapFacade?.zoomToDataAvoidingWindows?.(feature, 25);
   };

   const saveAll = async () => {
      if (!selectedProject) return;
      const validationErrors = validatePLAData(selectedProject, elements, relations);
      if (validationErrors.length > 0) {
         appMessageDispatch(actionSetErrorMessage(validationErrors.join("\n")));
         return;
      }
      try {
         const service = dataService(selectedProject.esIndexName);
         await Promise.all([...elements.map((element) => service.saveElement(element)), ...relations.map((relation) => service.saveRelation(relation))]);
         setDirty(false);
         appMessageDispatch(actionSetInfoMessage(Localization.getText("Project saved")));
      } catch (error: any) {
         appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to save project")}: ${error.message || error}`));
      }
   };

   const handleClose = () => {
      if (dirty) {
         setConfirmClose(true);
         return;
      }
      removePLAMapLayers(mapFacade?.getMapPop?.());
      windowDispatch(closeWindow(WindowId.PLAWindow));
   };

   const saveElementLocal = (element: PLAElementRecord) => {
      setElements((prev) => {
         const exists = prev.some((item) => item.id === element.id);
         return exists ? prev.map((item) => item.id === element.id ? element : item) : [...prev, element];
      });
      setDirty(true);
      setShowElementEditor(undefined);
   };

   const deleteElementLocal = (element: PLAElementRecord) => {
      setElements((prev) => prev.filter((item) => item.id !== element.id));
      setRelations((prev) => prev.filter((relation) => relation.fromId !== element.id && relation.toId !== element.id));
      setDirty(true);
      setConfirmDeleteElement(undefined);
   };

   const renderElementTypeTabTitle = (elementTypeId: string, elementType: PLAProject["elementTypes"][string]) => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
         <input
            type="checkbox"
            checked={visibleElementTypes[elementTypeId] !== false}
            aria-label={Localization.getText("Show element type on map")}
            title={Localization.getText("Show element type on map")}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
               const checked = event.target.checked;
               setVisibleElementTypes((prev) => ({ ...prev, [elementTypeId]: checked }));
            }}
         />
         <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: elementType.color || "#2d8f85" }} />
         {elementType.name || elementTypeId}
         {alertCountByElementType[elementTypeId] ? <span title={Localization.getText("Alerts")} style={{ color: "#d71920", fontWeight: 700, marginLeft: 2 }}>!</span> : null}
      </span>
   );

   return (
      <AdvancedDragModal
         title={`${Localization.getText("PlanIT")}${dirty ? " *" : ""}`}
         window={props.managedWindow}
         onClose={handleClose}
         allowUserMiniMize={true}
         allowPopout={true}
         allowFullscreen={true}
         PosDefault={{ top: "12px", left: "calc(100% - 12px)", transform: "translateX(-100%)", width: "1100px" }}
         PositionToSave={{ left: true, top: true, width: true, height: true }}
      >
         <ADModalBody>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
               <GlassButtonRow>
                  <select value={selectedProjectId} onChange={(event) => setSelectedProjectId(event.target.value)}>
                     <option value="">{Localization.getText("Choose project")}</option>
                     {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                  </select>
                  {dirty && <span>{Localization.getText("Unsaved changes")}</span>}
                  {!readOnly && <GlassButton onClick={saveAll} disabled={!dirty || !selectedProject}>{Localization.getText("Save")}</GlassButton>}
               </GlassButtonRow>
               {selectedProject && <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                  <div>
                     <strong>{selectedProject.name}</strong>
                     <div>{selectedProject.description}</div>
                  </div>
                  <GlassButtonRow adjust="end">
                     <GlassButton onClick={() => undefined}>{Localization.getText("Change log")}</GlassButton>
                     <GlassButtonDropdown
                        placement="bottom-end"
                        options={[
                           { text: Localization.getText("Parcels"), value: "matrikler" },
                           { text: Localization.getText("Buildings"), value: "bygninger" },
                           { text: Localization.getText("Consistency report"), value: "konsistensrapport" }
                        ]}
                        onSelect={() => undefined}
                     >
                        {Localization.getText("Reports")}
                     </GlassButtonDropdown>
                  </GlassButtonRow>
               </div>}
               {loading ? <Spinner /> : null}
               {selectedProject && <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key || undefined)}>
                  {Object.entries(selectedProject.elementTypes || {}).sort((a, b) => a[1].sortOrder - b[1].sortOrder).map(([elementTypeId, elementType]) => (
                     <Tab key={elementTypeId} eventKey={elementTypeId} title={renderElementTypeTabTitle(elementTypeId, elementType)}>
                        <PLATable
                           ref={(ref) => { tableRefs.current[elementTypeId] = ref; }}
                           project={selectedProject}
                           elementTypeId={elementTypeId}
                           elementType={elementType}
                           elements={elements.filter((element) => element.elementType === elementTypeId)}
                           allElements={elements}
                           relations={relations}
                           selectedElementId={selectedElementId}
                           readOnly={readOnly}
                           onRowClick={zoomToElement}
                           onAddElement={() => setShowElementEditor(createEmptyPLAElement(selectedProject, elementTypeId))}
                           onOpenElement={setShowElementEditor}
                           onCreateRelation={setShowRelationEditor}
                           onDeleteElement={setConfirmDeleteElement}
                        />
                     </Tab>
                  ))}
               </Tabs>}
            </div>
         </ADModalBody>
         {showElementEditor && selectedProject ? createPortal(
            <PLAElementDialog
               project={selectedProject}
               element={showElementEditor}
               elements={elements}
               relations={relations}
               availableLayers={availableLayers}
               readOnly={readOnly}
               onClose={() => setShowElementEditor(undefined)}
               onSave={saveElementLocal}
               onCreateRelation={setShowRelationEditor}
               onDeleteRelation={(relationId) => {
                  setRelations((prev) => prev.filter((relation) => relation.id !== relationId));
                  setDirty(true);
               }}
            />,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
         {showRelationEditor && selectedProject ? createPortal(
            <PLARelationDialog
               project={selectedProject}
               sourceElement={showRelationEditor}
               elements={elements}
               onClose={() => setShowRelationEditor(undefined)}
               onSave={(relation) => {
                  setRelations((prev) => [...prev, relation]);
                  setDirty(true);
                  setShowRelationEditor(undefined);
               }}
            />,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
         {confirmDeleteElement ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Confirm deletion")} onSubmit={() => deleteElementLocal(confirmDeleteElement)} onCancel={() => setConfirmDeleteElement(undefined)}>
               {Localization.getText("Confirm delete of element {elementId}?").replace("{elementId}", confirmDeleteElement.name)}
            </ConfirmationDialog>,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
         {confirmClose ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Unsaved changes")} onSubmit={() => {
               setConfirmClose(false);
               setDirty(false);
               removePLAMapLayers(mapFacade?.getMapPop?.());
               windowDispatch(closeWindow(WindowId.PLAWindow));
            }} onCancel={() => setConfirmClose(false)}>
               {Localization.getText("Close without saving?")}
            </ConfirmationDialog>,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
      </AdvancedDragModal>
   );
}
